import { usePrevious } from '@tamagui/use-previous'
import { type ComponentRef, useEffect, useRef, useState } from 'react'
import useAppState from '../../hooks/useAppState'
import { SlideInModal } from '../SlideInModal'
import { View, Image, Platform, Modal, TouchableOpacity, StyleSheet } from 'react-native'
import { Button } from '../Button'
import { router } from 'expo-router'
import { useReferralStats } from '../../hooks/useReferralStats'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useMediaQuery } from 'react-responsive'
import { routerBack } from '../../utils/routerBack'
import { Text } from 'react-native'
import { useIsUserSignedIn } from '../../hooks/useIsUserSignedIn'
import { mesaColorNames, typography } from '@/src/common/theme'

export function useIsNewReferralNotificationPresentedAsModal() {
  return !useMediaQuery({ maxWidth: 720 })
}

export function NewReferralNotification() {
  const { isAppVisible } = useAppState()
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { isSignedIn } = useIsUserSignedIn()
  const { data: referralStatsData, refetch } = useReferralStats()
  const isNewReferralNotificationPresentedAsModal = useIsNewReferralNotificationPresentedAsModal()
  const previousReferralStats = usePrevious(referralStatsData)
  const bottomSheetRef = useRef<ComponentRef<typeof SlideInModal>>(null)
  const [numberOfNewReferrals, setNumberOfNewReferrals] = useState(0)
  const [numberOfNewReferralPoints, setNumberOfNewReferralPoints] = useState(0)
  const [visible, setVisible] = useState(false)

  const previousNumberOfReferredMembers =
    previousReferralStats?.getReferralStats.CARD.numberOfReferredMembers || 0
  const previousReferralPoints = previousReferralStats?.getReferralStats.CARD.pointsEarned || 0
  const currentNumberOfReferredMembers =
    referralStatsData?.getReferralStats.CARD.numberOfReferredMembers || 0
  const currentReferralPoints = referralStatsData?.getReferralStats.CARD.pointsEarned || 0

  useEffect(() => {
    if (isAppVisible && isSignedIn) {
      void refetch()
    }
  }, [isAppVisible, refetch, isSignedIn])

  useEffect(() => {
    if (
      currentNumberOfReferredMembers > previousNumberOfReferredMembers &&
      currentReferralPoints > previousReferralPoints
    ) {
      setNumberOfNewReferrals(currentNumberOfReferredMembers - previousNumberOfReferredMembers)
      setNumberOfNewReferralPoints(currentReferralPoints - previousReferralPoints)
      setVisible(true)
    }
  }, [
    currentNumberOfReferredMembers,
    currentReferralPoints,
    previousNumberOfReferredMembers,
    previousReferralPoints,
  ])

  useEffect(() => {
    if (!isNewReferralNotificationPresentedAsModal && visible) {
      bottomSheetRef.current?.expand()
    } else {
      bottomSheetRef.current?.close()
    }
  }, [visible, isNewReferralNotificationPresentedAsModal, bottomSheetRef])

  const { bottom } = useSafeAreaInsets()

  // Render both the modal and the bottom sheet always, but only show one at a time
  // based on the screen size
  return (
    <>
      <Modal
        visible={isNewReferralNotificationPresentedAsModal && visible}
        transparent
        animationType="fade"
      >
        <TouchableOpacity
          activeOpacity={1}
          style={[{ backgroundColor: '#00000050', flex: 1 }, StyleSheet.absoluteFill]}
          onPress={() => {
            setVisible(false)
          }}
        />
        <View
          style={{
            margin: 'auto',
            flexBasis: 'auto',
            alignItems: 'center',
            justifyContent: 'center',
            marginHorizontal: 'auto',
            width: 720,
            maxWidth: '88%',
            maxHeight: '88%',
            borderRadius: 16,
            overflow: 'hidden',
            padding: 16,
            backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
          }}
        >
          <Image
            source={require('@/src/assets/images/coins.png')}
            resizeMode="contain"
            style={{
              width: 156,
              height: 156,
            }}
          />
          <View style={{ gap: 16 }}>
            <Text style={{ ...typography['paragraph/sm'], textAlign: 'center' }}>
              Congrats! You just earned {numberOfNewReferralPoints.toLocaleString()} points from
              your referral{numberOfNewReferrals > 1 ? 's' : ''}! Keep earning—refer more friends
              and get more points!
            </Text>
            <Button
              onPress={() => {
                void routerBack()
                setVisible(false)
                router.push('/main/refer-a-friend')
              }}
            >
              <Text style={typography['label/md-bold']}>Refer a friend</Text>
            </Button>
          </View>
        </View>
      </Modal>
      <SlideInModal
        ref={bottomSheetRef}
        title={numberOfNewReferrals > 1 ? 'New Referrals' : 'New Referral'}
        containerStyle={{
          gap: 32,
          alignItems: 'center',
          paddingBottom: Platform.OS === 'web' ? 32 : bottom,
        }}
        headerStyle={{
          marginBottom: 0,
        }}
        onClose={() => {
          setVisible(false)
        }}
      >
        <Image
          source={require('@/src/assets/images/coins.png')}
          resizeMode="contain"
          style={{
            width: 156,
            height: 156,
          }}
        />
        <View style={{ gap: 16 }}>
          <Text style={{ ...typography['paragraph/sm'], textAlign: 'center' }}>
            Congrats! You just earned {numberOfNewReferralPoints.toLocaleString()} points from your
            referral{numberOfNewReferrals > 1 ? 's' : ''}! Keep earning—refer more friends and get
            more points!
          </Text>
          <Button
            onPress={() => {
              bottomSheetRef.current?.close()
              setVisible(false)
              router.push('/main/refer-a-friend')
            }}
          >
            <Text style={typography['label/md-bold']}>Refer a friend</Text>
          </Button>
        </View>
      </SlideInModal>
    </>
  )
}
